import Phaser from 'phaser';
import { getMinigame } from '@/data/minijuegos';
import { GameState } from '@/engine/GameState';
import { ensureRunning, SCENE_KEYS } from '@/engine/nav';

/** Paleta coherente con el resto de escenas (tonos cálidos tierra + acento). */
const COLOR_FONDO = 0x14141c;
const COLOR_SUELO = 0x6b4423;
const COLOR_SUELO_OSCURO = 0x3b2412;
const COLOR_TRONCO = 0x5a3a1f;
const COLOR_TRONCO_VETA = 0x3b2412;
const COLOR_BOLA = 0xffffff;
const COLOR_BOLA_SOMBRA = 0x000000;
const COLOR_ANILLO_BUENO = 0x2ecc71;
const COLOR_ANILLO_REGULAR = 0xf1c40f;
const COLOR_TEXTO = '#ffffff';
const COLOR_TEXTO_DIM = '#8a8a95';
const COLOR_PANEL = 0x22222e;
const COLOR_BOTON = 0x2a2a38;
const COLOR_BOTON_HOVER = 0x38384a;

/** Geometría de la pista (en portrait 720x1280 se ajusta por scale.width/height). */
const HUD_ALTO = 100;
const SUELO_GROSOR = 80;
const TRONCO_ALTO = 22;
const TRONCO_ANCHO_FRACC = 0.55; // % del ancho de pantalla
const BOLA_RADIO = 16;

/** Balance del minijuego. */
const PUNTOS_PARA_GANAR = 5;
const PROB_FALLO_RIVAL = 0.35; // ~35% — ajustado para que cueste ganar

/** Timing del anillo de golpe. */
const ANILLO_RADIO_MAX = 70;
const ANILLO_RADIO_MIN = 14; // por debajo de esto = PERFECTO
const ANILLO_RADIO_OK = 38; // por debajo de esto = BUENO, por encima = TARDE
const TIEMPO_RITMO_MS = 1100; // lo que tarda el anillo en encogerse del máximo al 0

/** Tiempos de la parábola bola (se acortan con cada golpe para acelerar el rally). */
const DURACION_VUELTA_BASE_MS = 900;
const DURACION_VUELTA_MIN_MS = 520;
const ACELERACION_POR_GOLPE_MS = 35;

/** Datos opcionales que la escena recibe al iniciarse. Si `volverA` está
 *  definida, el botón SEGUIR navega ahí en lugar de emitir el evento
 *  global de minijuego-terminado. */
interface MinigamePachangaData {
  volverA?: string;
}

/**
 * Minijuego "La pachanga de fútbol casa 2v2" — timing de golpe sobre la
 * bola mientras cruza el tronco en parábola. Vista lateral: pista de tierra
 * bajo los árboles, el tronco hace de red.
 *
 * Contrato: al terminar emite `game.events.emit('minijuego-terminado',
 * { exito })` y hace scene.stop(). Si se lanzó con `volverA`, navega a
 * esa escena en lugar de emitir el evento (modo debug/test).
 */
export class MinigamePachangaScene extends Phaser.Scene {
  private volverA: string | null = null;

  /** Marcador de la pachanga. */
  private marcador: { vosotros: number; ellos: number } = { vosotros: 0, ellos: 0 };

  /** Estado del rally en curso. */
  private rallyEnCurso = false;
  private rallyGolpes = 0;

  /** Bola y su sombra en el suelo. */
  private bola!: Phaser.GameObjects.Arc;
  private bolaSombra!: Phaser.GameObjects.Ellipse;

  /** Anillo de timing que se encoge sobre la bola del jugador. */
  private anillo!: Phaser.GameObjects.Arc;
  private anilloTween: Phaser.Tweens.Tween | null = null;
  /** Tamaño del anillo en el momento del tap (para juzgar la calidad). */
  private anilloRadioActual = ANILLO_RADIO_MAX;

  /** Gráficos estáticos (se dibujan una vez). */
  private ambiente!: Phaser.GameObjects.Container;

  /** Marcador UI grande. */
  private textoMarcador!: Phaser.GameObjects.Text;

  /** Texto de feedback (¡Punto!, ¡La liáis!, ...) que aparece y se desvanece. */
  private textoFeedback!: Phaser.GameObjects.Text;

  /** Layout calculado en create(). */
  private pista = {
    ancho: 0,
    alto: 0,
    sueloY: 0,
    jugadorX: 0,
    rivalX: 0,
    jugadorY: 0,
    rivalY: 0,
    troncoY: 0,
    picoAlto: 0,
  };

  /** Zonas X para que el muñeco "salte" cuando le toca la bola. */
  private jugadorSaltoTween: Phaser.Tweens.Tween | null = null;
  private rivalSaltoTween: Phaser.Tweens.Tween | null = null;
  private jugadorGrupo!: Phaser.GameObjects.Container;
  private rivalGrupo!: Phaser.GameObjects.Container;

  constructor() {
    // La key DEBE coincidir con el id del minijuego en src/data/minijuegos.ts
    // (MissionScene mira scene.manager.keys[minijuegoId]).
    super('mg-pachanga');
  }

  init(data?: MinigamePachangaData): void {
    this.volverA = data?.volverA ?? null;
    this.marcador = { vosotros: 0, ellos: 0 };
    this.rallyEnCurso = false;
    this.rallyGolpes = 0;
  }

  create(): void {
    const { width, height } = this.scale;
    this.cameras.main.setBackgroundColor(COLOR_FONDO);

    ensureRunning(this, SCENE_KEYS.HUD);
    this.scene.bringToTop(SCENE_KEYS.HUD);

    // Layout de la pista.
    this.pista = {
      ancho: width,
      alto: height,
      sueloY: height - 120,
      jugadorX: width * 0.18,
      rivalX: width * 0.82,
      jugadorY: height - 120 - 80,
      rivalY: height - 120 - 80,
      troncoY: height - 120 - 60,
      picoAlto: 220,
    };

    this.ambiente = this.add.container(0, 0);
    this.dibujarAmbiente(width, height);
    this.dibujarJugadores();
    this.dibujarBola();

    this.dibujarMarcador();
    this.dibujarFeedback();

    this.configurarInput();
    this.refrescarMarcador();

    // Arranca el primer rally tras un breve delay para que se asienten las cosas.
    this.time.delayedCall(500, () => this.iniciarRally());
  }

  // ---------------------------------------------------------------------
  // Ambiente (fondo, suelo, árboles, tronco)
  // ---------------------------------------------------------------------

  private dibujarAmbiente(width: number, _height: number): void {
    const sueloY = this.pista.sueloY;

    // Cielo/atardecer en degradado simulado con bandas.
    const cielo = this.add.graphics();
    cielo.fillStyle(0x1a2030, 1);
    cielo.fillRect(0, HUD_ALTO, width, sueloY - HUD_ALTO);
    cielo.fillStyle(0x2a2538, 1);
    cielo.fillRect(0, sueloY - 200, width, 200);
    this.ambiente.add(cielo);

    // Árboles al fondo (silueta simple de copas redondas + troncos).
    this.dibujarArboles(width, sueloY);

    // Suelo de tierra.
    const suelo = this.add.graphics();
    suelo.fillStyle(COLOR_SUELO, 1);
    suelo.fillRect(0, sueloY, width, SUELO_GROSOR);
    suelo.fillStyle(COLOR_SUELO_OSCURO, 1);
    suelo.fillRect(0, sueloY + SUELO_GROSOR * 0.55, width, SUELO_GROSOR * 0.45);
    // Pequeñas irregularidades de tierra (puntitos).
    for (let i = 0; i < 30; i++) {
      const x = (i * 47) % width;
      const y = sueloY + ((i * 31) % SUELO_GROSOR);
      suelo.fillStyle(0x4a2f1a, 1);
      suelo.fillRect(x, y, 3, 2);
    }
    this.ambiente.add(suelo);

    // Tronco central (la "red"): rect marrón redondeado tumbado con vetas.
    const troncoAncho = width * TRONCO_ANCHO_FRACC;
    const troncoX = width / 2;
    const troncoY = this.pista.troncoY;
    const tronco = this.add.graphics();
    tronco.fillStyle(COLOR_TRONCO, 1);
    tronco.fillRoundedRect(
      troncoX - troncoAncho / 2,
      troncoY - TRONCO_ALTO / 2,
      troncoAncho,
      TRONCO_ALTO,
      TRONCO_ALTO / 2,
    );
    // Vetas del tronco (líneas finas horizontales).
    tronco.lineStyle(1, COLOR_TRONCO_VETA, 0.6);
    for (let i = 1; i < 6; i++) {
      const yVeta = troncoY - TRONCO_ALTO / 2 + (TRONCO_ALTO / 6) * i;
      tronco.beginPath();
      tronco.moveTo(troncoX - troncoAncho / 2 + 8, yVeta);
      tronco.lineTo(troncoX + troncoAncho / 2 - 8, yVeta);
      tronco.strokePath();
    }
    // Pequeñas raíces en los extremos.
    tronco.fillStyle(COLOR_TRONCO_VETA, 1);
    tronco.fillCircle(troncoX - troncoAncho / 2 + 2, troncoY, 6);
    tronco.fillCircle(troncoX + troncoAncho / 2 - 2, troncoY, 6);
    this.ambiente.add(tronco);

    // Etiqueta discreta "LA PISTA" en una esquina.
    this.ambiente.add(
      this.add
        .text(width / 2, HUD_ALTO + 16, 'Pista de tierra bajo la casa', {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '13px',
          color: COLOR_TEXTO_DIM,
        })
        .setOrigin(0.5),
    );
  }

  private dibujarArboles(width: number, sueloY: number): void {
    const g = this.add.graphics();
    // Troncos finos.
    const posiciones = [0.08, 0.22, 0.45, 0.68, 0.92];
    posiciones.forEach((frac, i) => {
      const x = width * frac;
      const troncoAlto = 120 + ((i * 37) % 40);
      g.fillStyle(0x2c1810, 1);
      g.fillRect(x - 4, sueloY - troncoAlto, 8, troncoAlto);
      // Copa redonda.
      g.fillStyle(0x1f3a1f, 1);
      g.fillCircle(x, sueloY - troncoAlto - 12, 36);
      g.fillStyle(0x2d5a2d, 0.8);
      g.fillCircle(x - 8, sueloY - troncoAlto - 8, 24);
    });
    this.ambiente.add(g);
  }

  // ---------------------------------------------------------------------
  // Jugadores (muñecos) — 2 izquierda, 2 derecha
  // ---------------------------------------------------------------------

  private dibujarJugadores(): void {
    const jugadorX = this.pista.jugadorX;
    const rivalX = this.pista.rivalX;
    const y = this.pista.jugadorY;

    this.jugadorGrupo = this.crearParejaMuñecos(jugadorX, y, ['🧑', '👦'], true);
    this.rivalGrupo = this.crearParejaMuñecos(rivalX, y, ['👨', '🧔'], false);
    this.ambiente.add(this.jugadorGrupo);
    this.ambiente.add(this.rivalGrupo);
  }

  /** Crea dos muñecos (círculos con emoji) como un Container. */
  private crearParejaMuñecos(
    xCentro: number,
    y: number,
    emojis: [string, string],
    ladoIzquierdo: boolean,
  ): Phaser.GameObjects.Container {
    const contenedor = this.add.container(xCentro, y);
    const radio = 24;
    const offsetX = 28;

    // Posiciones: el delantero más cerca del centro, el compañero detrás.
    const delanteroOffset = ladoIzquierdo ? offsetX : -offsetX;
    const companeroOffset = ladoIzquierdo ? -offsetX : offsetX;

    const delantero = this.crearMuñeco(delanteroOffset, 0, radio, emojis[0]);
    const companero = this.crearMuñeco(companeroOffset, -10, radio * 0.85, emojis[1]);

    contenedor.add([delantero, companero]);
    return contenedor;
  }

  private crearMuñeco(
    offsetX: number,
    offsetY: number,
    radio: number,
    emoji: string,
  ): Phaser.GameObjects.Container {
    const grupo = this.add.container(offsetX, offsetY);

    const cuerpo = this.add.graphics();
    cuerpo.fillStyle(0x2c2c38, 1);
    cuerpo.fillCircle(0, 0, radio);
    cuerpo.lineStyle(2, 0x14141c, 1);
    cuerpo.strokeCircle(0, 0, radio);

    const cara = this.add
      .text(0, 0, emoji, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: `${Math.round(radio * 1.2)}px`,
      })
      .setOrigin(0.5);

    grupo.add([cuerpo, cara]);
    return grupo;
  }

  // ---------------------------------------------------------------------
  // Bola y su sombra
  // ---------------------------------------------------------------------

  private dibujarBola(): void {
    const inicioX = this.pista.jugadorX;
    const inicioY = this.pista.jugadorY - 30;

    // Sombra en el suelo.
    this.bolaSombra = this.add.ellipse(inicioX, this.pista.sueloY - 4, BOLA_RADIO * 2, 8, COLOR_BOLA_SOMBRA, 0.35);

    // Bola.
    this.bola = this.add.circle(inicioX, inicioY, BOLA_RADIO, COLOR_BOLA, 1);
    this.bola.setStrokeStyle(2, 0xcccccc, 1);

    // Anillo de timing (oculto hasta que la bola vuelve a nuestro lado).
    this.anillo = this.add.circle(inicioX, inicioY, ANILLO_RADIO_MAX, COLOR_ANILLO_BUENO, 0);
    this.anillo.setStrokeStyle(4, COLOR_ANILLO_BUENO, 0.9);
    this.anillo.setVisible(false);
  }

  // ---------------------------------------------------------------------
  // Marcador y feedback
  // ---------------------------------------------------------------------

  private dibujarMarcador(): void {
    const { width } = this.scale;
    this.textoMarcador = this.add
      .text(width / 2, HUD_ALTO + 50, '', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '34px',
        fontStyle: 'bold',
        color: COLOR_TEXTO,
        align: 'center',
      })
      .setOrigin(0.5);
  }

  private refrescarMarcador(): void {
    this.textoMarcador.setText(
      `VOSOTROS ${this.marcador.vosotros}  -  ${this.marcador.ellos} ELLOS`,
    );
  }

  private dibujarFeedback(): void {
    const { width, height } = this.scale;
    this.textoFeedback = this.add
      .text(width / 2, height * 0.42, '', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '40px',
        fontStyle: 'bold',
        color: COLOR_TEXTO,
        align: 'center',
        stroke: '#000000',
        strokeThickness: 4,
      })
      .setOrigin(0.5);
    this.textoFeedback.setVisible(false);
  }

  /** Muestra un grito de texto que sube y se desvanece. */
  private mostrarFeedback(texto: string, exito: boolean): void {
    this.textoFeedback.setText(texto);
    this.textoFeedback.setColor(exito ? '#2ecc71' : '#e74c3c');
    this.textoFeedback.setAlpha(1);
    this.textoFeedback.setVisible(true);
    this.textoFeedback.y = this.scale.height * 0.42;

    this.tweens.add({
      targets: this.textoFeedback,
      y: this.textoFeedback.y - 40,
      alpha: 0,
      duration: 900,
      ease: 'Cubic.easeOut',
      onComplete: () => this.textoFeedback.setVisible(false),
    });
  }

  // ---------------------------------------------------------------------
  // Input
  // ---------------------------------------------------------------------

  private configurarInput(): void {
    // Tap en cualquier sitio cuenta como golpe.
    this.input.on('pointerdown', () => this.intentarGolpe());
    // Barra espaciadora también (mapeada por Phaser por defecto).
    this.input.keyboard?.on('keydown-SPACE', () => this.intentarGolpe());
  }

  // ---------------------------------------------------------------------
  // Lógica del rally
  // ---------------------------------------------------------------------

  /** Lanza un nuevo rally: bola en lado jugador, anillo listo para encoger. */
  private iniciarRally(): void {
    if (this.rallyEnCurso) return;
    this.rallyEnCurso = true;
    this.rallyGolpes = 0;

    // Posiciona bola en tu lado.
    const inicioX = this.pista.jugadorX;
    const inicioY = this.pista.jugadorY - 30;
    this.bola.setPosition(inicioX, inicioY);
    this.bolaSombra.setPosition(inicioX, this.pista.sueloY - 4);
    this.bolaSombra.setScale(1);

    // Anillo visible y listo para encoger.
    this.anillo.setPosition(inicioX, inicioY);
    this.anillo.setVisible(true);
    this.anillo.setRadius(ANILLO_RADIO_MAX);
    this.anillo.setStrokeStyle(4, COLOR_ANILLO_BUENO, 0.9);
    this.anilloRadioActual = ANILLO_RADIO_MAX;

    if (this.anilloTween) this.anilloTween.stop();
    this.anilloTween = this.tweens.add({
      targets: this.anillo,
      radius: ANILLO_RADIO_MIN * 0.3,
      duration: TIEMPO_RITMO_MS,
      ease: 'Linear',
      onUpdate: () => {
        this.anilloRadioActual = this.anillo.radius;
        // Cambia color: verde → amarillo → rojo.
        if (this.anillo.radius < ANILLO_RADIO_MIN) {
          this.anillo.setStrokeStyle(4, COLOR_ANILLO_BUENO, 0.95);
        } else if (this.anillo.radius < ANILLO_RADIO_OK) {
          this.anillo.setStrokeStyle(4, COLOR_ANILLO_BUENO, 0.9);
        } else if (this.anillo.radius < ANILLO_RADIO_OK + 18) {
          this.anillo.setStrokeStyle(4, COLOR_ANILLO_REGULAR, 0.9);
        } else {
          this.anillo.setStrokeStyle(4, COLOR_ANILLO_REGULAR, 0.7);
        }
      },
      onComplete: () => {
        // Se acabó el tiempo sin tocar: punto para ELLOS.
        if (this.rallyEnCurso) {
          this.anularAnillo();
          this.puntoParaEllos('¡La liasteis!');
        }
      },
    });
  }

  private anularAnillo(): void {
    if (this.anilloTween) {
      this.anilloTween.stop();
      this.anilloTween = null;
    }
    this.anillo.setVisible(false);
  }

  /** Intenta golpear la bola (tap o espacio). Solo vale si el anillo está
   *  visible: si no, el tap se ignora (estamos en medio de un vuelo rival). */
  private intentarGolpe(): void {
    if (!this.rallyEnCurso) return;
    if (!this.anillo.visible) return;

    const radio = this.anilloRadioActual;
    this.anularAnillo();

    let calidad: 'perfecto' | 'bueno' | 'tarde';
    if (radio < ANILLO_RADIO_MIN) {
      calidad = 'perfecto';
    } else if (radio < ANILLO_RADIO_OK) {
      calidad = 'bueno';
    } else if (radio < ANILLO_RADIO_OK + 18) {
      calidad = 'tarde'; // golpe aceptable pero no ideal
    } else {
      // Muy tarde: punto para el rival.
      this.puntoParaEllos('¡Se te fue!');
      return;
    }

    // Golpe válido: la bola sale disparada al lado rival.
    this.saltarMuñeco(this.jugadorGrupo, -1);
    this.enviarBolaAlRival(calidad);
  }

  /** Animación parábola de la bola desde jugador hacia rival. */
  private enviarBolaAlRival(calidad: 'perfecto' | 'bueno' | 'tarde'): void {
    this.rallyGolpes += 1;

    const duracion = Math.max(
      DURACION_VUELTA_MIN_MS,
      DURACION_VUELTA_BASE_MS - this.rallyGolpes * ACELERACION_POR_GOLPE_MS,
    );
    // Golpe perfecto: parábola más tensa y rápida.
    const factorCalidad = calidad === 'perfecto' ? 0.78 : calidad === 'bueno' ? 1 : 1.1;
    const dur = duracion * factorCalidad;
    const pico = this.pista.picoAlto * (calidad === 'perfecto' ? 0.7 : 1);

    const inicioX = this.bola.x;
    const inicioY = this.bola.y;
    const finX = this.pista.rivalX;
    const finY = this.pista.rivalY - 30;
    const sueloY = this.pista.sueloY - 4;

    // Tween X lineal manual (Phaser no hace parábolas nativas).
    this.tweens.add({
      targets: this.bola,
      x: finX,
      duration: dur,
      ease: 'Linear',
      onUpdate: () => {
        // Interpolación de Y como parábola: t∈[0,1], y = lerp con pico central.
        const t = (this.bola.x - inicioX) / (finX - inicioX);
        const curva = 4 * t * (1 - t); // pico 1 en t=0.5
        this.bola.y = inicioY + (finY - inicioY) * t - curva * pico;
        // Sombra: escala varía con altura (más baja -> sombra más ancha).
        const altura = (this.pista.sueloY - 4) - this.bola.y;
        const escala = Phaser.Math.Clamp(0.5 + altura / 400, 0.5, 1.4);
        this.bolaSombra.setPosition(this.bola.x, sueloY);
        this.bolaSombra.setScale(escala, escala * 0.7);
      },
      onComplete: () => {
        this.rivalIntentaDevolver();
      },
    });
  }

  /** El rival "intenta" devolver: falla con probabilidad PROB_FALLO_RIVAL. */
  private rivalIntentaDevolver(): void {
    if (!this.rallyEnCurso) return;
    // Pequeño delay para que se sienta la reacción del rival.
    this.time.delayedCall(220, () => {
      if (!this.rallyEnCurso) return;
      const falla = Math.random() < PROB_FALLO_RIVAL;
      if (falla) {
        this.saltarMuñeco(this.rivalGrupo, 1);
        // Punto para vosotros.
        this.rallyEnCurso = false;
        this.marcador.vosotros += 1;
        this.refrescarMarcador();
        this.mostrarFeedback('¡Punto!', true);
        this.comprobarFin();
      } else {
        // Devuelven: la bola vuelve a tu lado.
        this.saltarMuñeco(this.rivalGrupo, 1);
        this.enviarBolaAlJugador();
      }
    });
  }

  /** Parábola rival → jugador. Al llegar, mostramos el anillo de timing. */
  private enviarBolaAlJugador(): void {
    const duracion = Math.max(
      DURACION_VUELTA_MIN_MS,
      DURACION_VUELTA_BASE_MS - this.rallyGolpes * ACELERACION_POR_GOLPE_MS,
    );
    const inicioX = this.bola.x;
    const inicioY = this.bola.y;
    const finX = this.pista.jugadorX;
    const finY = this.pista.jugadorY - 30;
    const sueloY = this.pista.sueloY - 4;
    const pico = this.pista.picoAlto;

    this.tweens.add({
      targets: this.bola,
      x: finX,
      duration: duracion,
      ease: 'Linear',
      onUpdate: () => {
        const t = (this.bola.x - inicioX) / (finX - inicioX);
        const curva = 4 * t * (1 - t);
        this.bola.y = inicioY + (finY - inicioY) * t - curva * pico;
        const altura = (this.pista.sueloY - 4) - this.bola.y;
        const escala = Phaser.Math.Clamp(0.5 + altura / 400, 0.5, 1.4);
        this.bolaSombra.setPosition(this.bola.x, sueloY);
        this.bolaSombra.setScale(escala, escala * 0.7);
      },
      onComplete: () => {
        // Llegó a tu lado: arranca el anillo de timing.
        this.anillo.setPosition(finX, finY);
        this.anillo.setVisible(true);
        this.anillo.setRadius(ANILLO_RADIO_MAX);
        this.anillo.setStrokeStyle(4, COLOR_ANILLO_BUENO, 0.9);
        this.anilloRadioActual = ANILLO_RADIO_MAX;

        if (this.anilloTween) this.anilloTween.stop();
        this.anilloTween = this.tweens.add({
          targets: this.anillo,
          radius: ANILLO_RADIO_MIN * 0.3,
          duration: TIEMPO_RITMO_MS,
          ease: 'Linear',
          onUpdate: () => {
            this.anilloRadioActual = this.anillo.radius;
            if (this.anillo.radius < ANILLO_RADIO_MIN) {
              this.anillo.setStrokeStyle(4, COLOR_ANILLO_BUENO, 0.95);
            } else if (this.anillo.radius < ANILLO_RADIO_OK) {
              this.anillo.setStrokeStyle(4, COLOR_ANILLO_BUENO, 0.9);
            } else if (this.anillo.radius < ANILLO_RADIO_OK + 18) {
              this.anillo.setStrokeStyle(4, COLOR_ANILLO_REGULAR, 0.9);
            } else {
              this.anillo.setStrokeStyle(4, COLOR_ANILLO_REGULAR, 0.7);
            }
          },
          onComplete: () => {
            if (this.rallyEnCurso) {
              this.anularAnillo();
              this.puntoParaEllos('¡La liasteis!');
            }
          },
        });
      },
    });
  }

  private puntoParaEllos(grito: string): void {
    if (!this.rallyEnCurso) return;
    this.rallyEnCurso = false;
    this.marcador.ellos += 1;
    this.refrescarMarcador();
    this.mostrarFeedback(grito, false);
    this.comprobarFin();
  }

  private saltarMuñeco(grupo: Phaser.GameObjects.Container, dir: number): void {
    const tween =
      grupo === this.jugadorGrupo ? this.jugadorSaltoTween : this.rivalSaltoTween;
    if (tween) tween.stop();
    const nuevo = this.tweens.add({
      targets: grupo,
      y: grupo.y + dir * -22,
      duration: 110,
      yoyo: true,
      ease: 'Quad.easeOut',
    });
    if (grupo === this.jugadorGrupo) this.jugadorSaltoTween = nuevo;
    else this.rivalSaltoTween = nuevo;
  }

  // ---------------------------------------------------------------------
  // Fin del partido + recompensas
  // ---------------------------------------------------------------------

  private comprobarFin(): void {
    if (this.marcador.vosotros >= PUNTOS_PARA_GANAR) {
      this.finalizar(true);
    } else if (this.marcador.ellos >= PUNTOS_PARA_GANAR) {
      this.finalizar(false);
    } else {
      // Siguiente rally tras un pequeño respiro.
      this.time.delayedCall(900, () => this.iniciarRally());
    }
  }

  /** Muestra panel final, aplica recompensas y prepara la salida. */
  private finalizar(exito: boolean): void {
    this.rallyEnCurso = false;
    this.anularAnillo();

    const mg = getMinigame('mg-pachanga');
    const respetoGanado = exito ? mg.recompensaBase.respeto : 1;
    const dineroGanado = exito ? mg.recompensaBase.dinero ?? 0 : 0;

    GameState.addRespeto(respetoGanado);
    if (dineroGanado > 0) GameState.addDinero(dineroGanado);
    GameState.registrarPartida('mg-pachanga');

    this.renderPanelResultado(mg.nombre, exito, respetoGanado, dineroGanado, () => this.salir(exito));
  }

  private renderPanelResultado(
    nombre: string,
    exito: boolean,
    respeto: number,
    dinero: number,
    onSeguir: () => void,
  ): void {
    const { width, height } = this.scale;
    const cx = width / 2;
    const cy = height / 2 - 40;

    const panelAncho = width - 48;
    const panelAlto = 360;

    const overlay = this.add.graphics();
    overlay.fillStyle(0x000000, 0.55);
    overlay.fillRect(0, 0, width, height);

    const panel = this.add.graphics();
    panel.fillStyle(COLOR_PANEL, 1);
    panel.fillRoundedRect(-panelAncho / 2, -panelAlto / 2, panelAncho, panelAlto, 20);
    panel.setPosition(cx, cy);

    this.add.text(cx, cy - 130, nombre, {
      fontFamily: 'system-ui, sans-serif',
      fontSize: '22px',
      fontStyle: 'bold',
      color: COLOR_TEXTO_DIM,
      align: 'center',
      wordWrap: { width: panelAncho - 40 },
    }).setOrigin(0.5);

    const titulo = exito ? '¡GANÁIS!' : '¡Os han metido un saco!';
    const colorTitulo = exito ? '#2ecc71' : '#e74c3c';
    this.add.text(cx, cy - 86, titulo, {
      fontFamily: 'system-ui, sans-serif',
      fontSize: '32px',
      fontStyle: 'bold',
      color: colorTitulo,
      align: 'center',
      wordWrap: { width: panelAncho - 40 },
    }).setOrigin(0.5);

    const marcadorFinal = `VOSOTROS ${this.marcador.vosotros}  -  ${this.marcador.ellos} ELLOS`;
    this.add.text(cx, cy - 30, marcadorFinal, {
      fontFamily: 'system-ui, sans-serif',
      fontSize: '26px',
      fontStyle: 'bold',
      color: COLOR_TEXTO,
    }).setOrigin(0.5);

    const partes: string[] = [`✊ +${respeto}`];
    if (dinero > 0) partes.push(`💶 +${dinero}`);
    const recompensas = partes.join('  ·  ');
    this.add.text(cx, cy + 30, recompensas, {
      fontFamily: 'system-ui, sans-serif',
      fontSize: '28px',
      fontStyle: 'bold',
      color: COLOR_TEXTO,
    }).setOrigin(0.5);

    // Botón SEGUIR.
    const anchoBoton = 260;
    const altoBoton = 76;
    const botonY = cy + 110;
    const boton = this.add.graphics();
    const dibujar = (color: number) => {
      boton.clear();
      boton.fillStyle(color, 1);
      boton.fillRoundedRect(-anchoBoton / 2, -altoBoton / 2, anchoBoton, altoBoton, 18);
    };
    dibujar(COLOR_BOTON);
    boton.setPosition(cx, botonY);

    this.add.text(cx, botonY, 'SEGUIR', {
      fontFamily: 'system-ui, sans-serif',
      fontSize: '22px',
      fontStyle: 'bold',
      color: COLOR_TEXTO,
    }).setOrigin(0.5);

    const zona = this.add.zone(cx, botonY, anchoBoton, altoBoton).setInteractive({ useHandCursor: true });
    zona.on('pointerover', () => dibujar(COLOR_BOTON_HOVER));
    zona.on('pointerout', () => dibujar(COLOR_BOTON));
    zona.on('pointerdown', () => dibujar(COLOR_BOTON_HOVER));
    zona.on('pointerup', () => {
      zona.disableInteractive();
      onSeguir();
    });
  }

  /** Emite el evento de fin de minijuego (o navega a volverA) y se detiene. */
  private salir(exito: boolean): void {
    if (this.volverA) {
      this.scene.start(this.volverA);
      return;
    }
    this.game.events.emit('minijuego-terminado', { exito });
    this.scene.stop();
  }
}